import { useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public newsletter signup — captures emails on the storefront.
 * `variant`: "ss" for the ShopSchoen cream palette (default on /shop),
 *            "hq" for the legacy dark admin theme.
 */
export function NewsletterSignup({ source = "shop", variant = "ss" }) {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = email.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/audience/newsletter/subscribe`, {
        email: v, lang, source,
      });
      setSubscribed(true);
      toast.success(t(lang, "newsletter_subscribed_toast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  };

  const ss = variant === "ss";
  const wrapCls = ss
    ? "rounded-lg border border-[color:var(--ss-border-strong)] bg-white p-6 sm:p-8"
    : "rounded-lg border border-white/10 bg-white/[0.02] p-5 sm:p-6";
  const titleCls = ss ? "ss-serif text-2xl text-[color:var(--ss-ink)]" : "font-display text-lg";
  const bodyCls  = ss ? "text-[color:var(--ss-ink-muted)] text-sm mb-4 max-w-lg" : "text-white/60 text-xs mb-4 max-w-lg";
  const inputCls = ss ? "ss-input flex-1 text-sm" : "hq-input flex-1 text-sm";
  const btnCls   = ss ? "ss-btn ss-btn-primary" : "hq-btn hq-btn-primary";
  const disclCls = ss
    ? "mt-3 ss-mono text-[10px] uppercase tracking-widest text-[color:var(--ss-ink-muted)]"
    : "mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40";
  const mailColor = ss ? "text-[color:var(--ss-gold)]" : "text-[color:var(--hq-cyan)]";

  if (subscribed) {
    return (
      <div
        data-testid="newsletter-subscribed"
        className={ss
          ? "rounded-lg border border-[color:var(--ss-gold)]/30 bg-[color:var(--ss-gold)]/[0.06] p-5 flex items-center gap-3"
          : "rounded-lg border border-[color:var(--hq-success)]/30 bg-[color:var(--hq-success)]/[0.06] p-5 flex items-center gap-3"}
      >
        <CheckCircle2 size={20} className={ss ? "text-[color:var(--ss-gold)]" : "text-[color:var(--hq-success)]"} />
        <div className={ss ? "text-sm text-[color:var(--ss-ink)]" : "text-sm text-white/80"}>
          {t(lang, "newsletter_subscribed_body")}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="newsletter-signup" className={wrapCls}>
      <div className="flex items-center gap-2 mb-2">
        <Mail size={16} className={mailColor} />
        <div className={titleCls}>{t(lang, "newsletter_title")}</div>
      </div>
      <p className={bodyCls}>{t(lang, "newsletter_body")}</p>
      <div className="flex flex-col sm:flex-row gap-2 max-w-lg">
        <input
          data-testid="newsletter-email-input"
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t(lang, "newsletter_email_placeholder")}
          className={inputCls}
        />
        <button
          data-testid="newsletter-submit-btn"
          type="submit" disabled={busy || !email.trim()}
          className={`${btnCls} disabled:opacity-50`}
        >
          {t(lang, "newsletter_subscribe")}
          <ArrowRight size={12} />
        </button>
      </div>
      <div className={disclCls}>{t(lang, "newsletter_disclaimer")}</div>
    </form>
  );
}
